/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { ArrowRightIcon, SearchIcon } from "lucide-react"
import type { SubmitEvent } from "react"
import { useRef, useState, useTransition } from "react"
import { useNavigate } from "react-router"

import { ApplicationSearchViewmodel } from "../../features/application-search/application-search-viewmodel"
import { getShortcutLabels, useKeyboardShortcut } from "../../utils/use-keyboard-shortcut"
import { useViewModel } from "../app/app-hooks"
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "../primitives/input-group"
import { Kbd, KbdGroup } from "../primitives/kbd"
import { Spinner } from "../primitives/spinner"
import { IconSwap } from "./icon-swap"

const FOCUS_SHORTCUT = "$mod+k"
const SHORTCUT_LABELS = getShortcutLabels(FOCUS_SHORTCUT)

/**
 * Finds a device by Client ID or Device UUID and opens the page the search leads to.
 */
export const ApplicationSearch = () => {
    const [query, setQuery] = useState("")
    const [isPending, startTransition] = useTransition()

    const inputRef = useRef<HTMLInputElement>(null)
    const trimmedQuery = query.trim()

    const navigate = useNavigate()
    const vm = useViewModel(ApplicationSearchViewmodel)

    useKeyboardShortcut(FOCUS_SHORTCUT, () => inputRef.current?.focus())

    const submit = (e: SubmitEvent<HTMLFormElement>) => {
        e.preventDefault()

        startTransition(async () => {
            const redirect = await vm.searchApplicationByQuery(trimmedQuery)
            if (redirect) {
                navigate(redirect)
            }
        })
    }

    return (
        <form role="search" aria-busy={isPending} className="w-90 max-w-full" onSubmit={submit}>
            <InputGroup className="bg-background">
                <InputGroupAddon>
                    <IconSwap icons={[SearchIcon, Spinner]} active={isPending ? 1 : 0} iconClassName="size-4" />
                </InputGroupAddon>
                <InputGroupInput
                    ref={inputRef}
                    placeholder="Enter Client ID or Device UUID"
                    aria-label="Client ID or Device UUID"
                    className="text-ellipsis"
                    // Preserve ID casing and prevent mobile keyboards from correcting IDs.
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
                <InputGroupAddon align="inline-end">
                    {/* Reboot styles the outer `<kbd>`; the hint is irrelevant on touch-only devices. */}
                    <KbdGroup className="bg-transparent p-0 group-focus-within/input-group:hidden pointer-coarse:hidden">
                        {SHORTCUT_LABELS.map((label) => (
                            <Kbd key={label}>{label}</Kbd>
                        ))}
                    </KbdGroup>
                    <InputGroupButton
                        type="submit"
                        size="icon-xs"
                        aria-label="Search"
                        disabled={isPending || !trimmedQuery}
                    >
                        <ArrowRightIcon />
                    </InputGroupButton>
                </InputGroupAddon>
            </InputGroup>
        </form>
    )
}
